import React, { useMemo, useState } from "react";

const BRAND = "#d9a42a";

const FACTORS = [
  { id: "speed", label: "Szybkość ładowania strony", weight: 10 },
  { id: "responsive", label: "Responsywność (mobile-first)", weight: 10 },
  { id: "checkout", label: "1-krokowy checkout / prosty proces zakupu", weight: 10 },
  { id: "guest", label: "Gościnne zakupy (bez rejestracji)", weight: 9 },
  { id: "payments", label: "Popularne metody płatności (BLIK, PayPal, PayU)", weight: 9 },
  { id: "shipping", label: "Wiele opcji dostawy (kurier, paczkomat, odbiór)", weight: 9 },
  { id: "transparent", label: "Transparentna cena (brak ukrytych kosztów)", weight: 9 },
  { id: "reviews", label: "Opinie i oceny klientów", weight: 9 },
  { id: "photos", label: "Wyraźne zdjęcia wysokiej jakości", weight: 9 },
  { id: "freeShipThreshold", label: "Darmowa dostawa od określonej kwoty", weight: 8 },
  { id: "crossSell", label: "Cross-sell / rekomendacje produktów", weight: 8 },
  { id: "cartPromo", label: "Rabat/promocja widoczna w koszyku", weight: 8 },
  { id: "cta", label: "Wyraźne i mocne CTA (np. ‘Kup teraz’)", weight: 8 },
  { id: "liveChat", label: "Live chat lub chatbot (szybka pomoc)", weight: 7 },
  { id: "urgency", label: "Licznik czasu / deadline promocji", weight: 7 },
  { id: "badges", label: "Badges ‘Bestseller’ / ‘Najczęściej kupowane’", weight: 7 },
  { id: "gift", label: "Gratisy do zamówienia (mały upominek)", weight: 6 },
  { id: "photoReviews", label: "Miniatury opinii ze zdjęciami klientów", weight: 8 },
  { id: "tiers", label: "Progi cenowe: ‘Kup za X zł, dostaniesz −Y%’", weight: 7 },
  { id: "security", label: "Bezpieczeństwo płatności (SSL, ikony zaufania)", weight: 10 }
];

const BASE = 0.75;
const MAX = 7.0;
const SUM_WEIGHTS = FACTORS.reduce((s, f) => s + f.weight, 0);
const UNIT = (MAX - BASE) / SUM_WEIGHTS;

function formatPct(val) {
  return `${val.toFixed(2)}%`;
}
function formatInt(n) {
  return Math.floor(n).toLocaleString("pl-PL");
}
function formatPLN(n) {
  return n.toLocaleString("pl-PL", { style: "currency", currency: "PLN", maximumFractionDigits: 0 });
}

export default function App() {
  const [checked, setChecked] = useState({});
  const [adSpend, setAdSpend] = useState(10000);
  const [cpc, setCpc] = useState(1.5);
  const [aov, setAov] = useState(200);

  const selectedWeight = useMemo(
    () => FACTORS.reduce((s, f) => s + (checked[f.id] ? f.weight : 0), 0),
    [checked]
  );

  const conversion = useMemo(() => {
    const inc = selectedWeight * UNIT;
    return Math.min(MAX, BASE + inc);
  }, [selectedWeight]);

  const visitors = useMemo(() => (cpc > 0 ? adSpend / cpc : 0), [adSpend, cpc]);
  const transactionsBase = useMemo(() => visitors * (BASE / 100), [visitors]);
  const transactionsCurr = useMemo(() => visitors * (conversion / 100), [visitors, conversion]);

  const revenueBase = useMemo(() => transactionsBase * aov, [transactionsBase, aov]);
  const revenueCurr = useMemo(() => transactionsCurr * aov, [transactionsCurr, aov]);

  const deltaTx = Math.max(0, Math.floor(transactionsCurr - transactionsBase));
  const deltaRev = Math.max(0, revenueCurr - revenueBase);

  const progress = Math.min(100, (conversion / MAX) * 100);

  const toggle = (id) => setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  const resetAll = () => setChecked({});

  return (
    <div className="min-h-screen w-full bg-neutral-950 text-white flex items-center justify-center p-4">
      <div className="w-full max-w-6xl">
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">
            Kalkulator konwersji e-commerce <span className="font-normal text-neutral-300">by Mateusz</span>
          </h1>
          <p className="text-neutral-300 mt-2 max-w-3xl">
            Zaznacz elementy, które masz wdrożone w sklepie. Każdy czynnik podnosi konwersję proporcjonalnie do swojej wagi. Start:{" "}
            <span style={{ color: BRAND }}>0,75%</span>.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* LEWA STRONA */}
          <div className="bg-neutral-900/70 rounded-2xl p-6 shadow-xl border border-neutral-800">
            <div className="flex items-center justify-between mb-4">
              <span className="text-neutral-300">Aktualna konwersja</span>
              <button
                onClick={resetAll}
                className="px-4 py-2 rounded-xl border border-neutral-700 hover:border-neutral-500 transition"
              >
                Reset czynników
              </button>
            </div>

            <div className="flex items-end gap-3 mb-4">
              <div className="text-5xl md:text-6xl font-bold" style={{ color: BRAND }}>
                {formatPct(conversion)}
              </div>
            </div>

            <div className="w-full h-3 bg-neutral-800 rounded-full overflow-hidden">
              <div className="h-full" style={{ width: `${progress}%`, backgroundColor: BRAND }} />
            </div>

            {/* SUWAKI */}
            <div className="mt-6 space-y-4">
              <h3 className="text-lg font-semibold" style={{ color: BRAND }}>
                Założenia kampanii
              </h3>

              {/* Ad spend */}
              <div>
                <div className="flex items-center justify-between text-sm mb-1">
                  <label className="text-neutral-300">Wydatki na reklamę</label>
                  <span className="font-medium" style={{ color: BRAND }}>
                    {formatPLN(adSpend)}
                  </span>
                </div>
                <input
                  type="range"
                  min={100}
                  max={100000}
                  step={100}
                  value={adSpend}
                  onChange={(e) => setAdSpend(Number(e.target.value))}
                  className="w-full"
                  style={{ accentColor: BRAND }}
                />
              </div>

              {/* CPC */}
              <div>
                <div className="flex items-center justify-between text-sm mb-1">
                  <label className="text-neutral-300">CPC</label>
                  <span className="font-medium" style={{ color: BRAND }}>
                    {cpc.toFixed(2)} zł
                  </span>
                </div>
                <input
                  type="range"
                  min={0.5}
                  max={5}
                  step={0.05}
                  value={cpc}
                  onChange={(e) => setCpc(Number(e.target.value))}
                  className="w-full"
                  style={{ accentColor: BRAND }}
                />
              </div>

              {/* AOV */}
              <div>
                <div className="flex items-center justify-between text-sm mb-1">
                  <label className="text-neutral-300">Średnia wartość koszyka (AOV)</label>
                  <span className="font-medium" style={{ color: BRAND }}>
                    {formatPLN(aov)}
                  </span>
                </div>
                <input
                  type="range"
                  min={20}
                  max={2000}
                  step={10}
                  value={aov}
                  onChange={(e) => setAov(Number(e.target.value))}
                  className="w-full"
                  style={{ accentColor: BRAND }}
                />
              </div>

              {/* KPI */}
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div className="bg-neutral-800/40 rounded-xl p-3 border border-neutral-800">
                  <div className="text-neutral-400 text-sm">Liczba wejść (ruch)</div>
                  <div className="text-2xl font-semibold" style={{ color: BRAND }}>
                    {formatInt(visitors)}
                  </div>
                </div>
                <div className="bg-neutral-800/40 rounded-xl p-3 border border-neutral-800">
                  <div className="text-neutral-400 text-sm">Transakcje (bazowo 0,75%)</div>
                  <div className="text-2xl font-semibold" style={{ color: BRAND }}>
                    {formatInt(transactionsBase)}
                  </div>
                </div>
                <div className="bg-neutral-800/40 rounded-2xl p-4 border border-neutral-700 col-span-2">
                  <div className="text-neutral-300 text-sm">Transakcje przy aktualnej konwersji</div>
                  <div className="text-4xl md:text-5xl font-bold" style={{ color: BRAND }}>
                    {formatInt(transactionsCurr)}
                  </div>
                </div>
                <div className="bg-neutral-800/40 rounded-xl p-3 border border-neutral-800">
                  <div className="text-neutral-400 text-sm">Przychód bazowy</div>
                  <div className="text-2xl font-semibold" style={{ color: BRAND }}>
                    {formatPLN(revenueBase)}
                  </div>
                </div>
                <div className="bg-neutral-800/40 rounded-xl p-3 border border-neutral-800">
                  <div className="text-neutral-400 text-sm">Przychód (aktualna konwersja)</div>
                  <div className="text-2xl font-semibold" style={{ color: BRAND }}>
                    {formatPLN(revenueCurr)}
                  </div>
                </div>
              </div>

              {/* Podsumowanie */}
              <div className="mt-6 bg-neutral-900 rounded-2xl p-6 border border-neutral-800">
                <h4 className="text-lg font-semibold mb-4" style={{ color: BRAND }}>
                  Podsumowanie wpływu konwersji
                </h4>
                <p className="mb-4 text-neutral-200 font-medium">
                  Utrata zysków jest ogromna, jeśli nie dbasz o konwersję!
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-neutral-950 rounded-xl p-4 border-2" style={{ borderColor: BRAND }}>
                    <div className="text-sm text-neutral-400">Dodatkowe transakcje</div>
                    <div className="text-5xl font-bold" style={{ color: BRAND }}>
                      +{formatInt(deltaTx)}
                    </div>
                  </div>
                  <div className="bg-neutral-950 rounded-xl p-4 border-2" style={{ borderColor: BRAND }}>
                    <div className="text-sm text-neutral-400">Dodatkowy przychód</div>
                    <div className="text-5xl font-bold" style={{ color: BRAND }}>
                      +{formatPLN(deltaRev)}
                    </div>
                  </div>
                </div>
                <p className="text-neutral-400 mt-4 text-xs">
                  To są realne straty, jakie ponosisz zostając przy konwersji 0,75%. Każdy wdrożony element to potencjalny zysk.
                </p>
              </div>
            </div>
          </div>

          {/* PRAWA STRONA */}
          <div className="bg-neutral-900/70 rounded-2xl p-6 shadow-xl border border-neutral-800">
            <h2 className="text-xl font-semibold mb-4" style={{ color: BRAND }}>
              Zaznacz wdrożone elementy (20)
            </h2>

            <div className="space-y-2 max-h-[70vh] overflow-auto pr-2 custom-scroll">
              {FACTORS.map((f) => (
                <label
                  key={f.id}
                  className="flex items-start gap-3 p-3 rounded-xl border border-neutral-800 hover:border-neutral-600 transition cursor-pointer bg-neutral-800/30"
                >
                  <input
                    type="checkbox"
                    checked={!!checked[f.id]}
                    onChange={() => toggle(f.id)}
                    className="mt-1 h-5 w-5 rounded border-neutral-700 focus:ring-0"
                    style={{ accentColor: BRAND }}
                  />
                  <div>
                    <div className="font-medium leading-snug">{f.label}</div>
                  </div>
                </label>
              ))}
            </div>

            <div className="mt-4 text-xs text-neutral-500">
              Kalkulacja jest szacunkowa i liniowa; rzeczywiste efekty zależą od branży i jakości ruchu.
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .custom-scroll::-webkit-scrollbar { width: 10px; }
        .custom-scroll::-webkit-scrollbar-track { background: #0a0a0a; border-radius: 9999px; }
        .custom-scroll::-webkit-scrollbar-thumb { background: ${BRAND}; border-radius: 9999px; }
      `}</style>
    </div>
  );
}
